import { createAction, type ActionCreatorWithPayload, type Slice } from '@reduxjs/toolkit';
import { entries } from 'lodash-es';

type InstanceID = string;

type InstanceAction<P = unknown, T extends string = string> = {
  type: T;
  payload?: P;
  meta?: { instanceID?: InstanceID };
};

export type KeyedState<S> = { [key: InstanceID]: S };

export const createDestroyKeyedStateAction = (sliceName: string) => createAction(`${sliceName}/destroy-keyed-state`);

/** For reducers you are making keyed, you can put your actions through this utility to also make your actions keyed.
 * Actions built using this function will need the implementer to pass in the instanceID on each call.
 * Used when you want to expose a set of actions that can be used for several instanceIDs.
 */
export function keyedAction<P>(ac: ActionCreatorWithPayload<P>) {
  return (payload: P, instanceID: InstanceID) => {
    return { ...ac(payload), meta: { instanceID } };
  };
}

/** Sibling to `keyedAction`, but is built with knowledge of the instanceID, and will automatically attach this instanceID
 * to each action when they are called. Used when you want to provide "scoped" actions for a specific instance.
 */
export function keyedActionWithInstanceID<P>(ac: ActionCreatorWithPayload<P>, instanceID: string) {
  return (payload: P) => {
    return { ...ac(payload), meta: { instanceID } };
  };
}

/**
 * Wrap your slice's reducer with a keyed reducer to allow for multiple state instances of your slice's state
 *
 * ```
 * { "some-instance-id": { ...your instance state }, "other-instance-id": { ...another instance state } }
 * ```
 *
 * You can write and test your slice as a singleton (mostly), and then use this keyedReducer concept to make it not-a-singleton.
 * See `keyedReducerAndListener.test.ts` for a minimal example of how to set things up
 *
 * Note: for destroying state instances, you need to use the fn `createDestroyKeyedStateAction`
 */
export function sliceToKeyedReducer<S, A extends InstanceAction = InstanceAction>(slice: Slice<S>) {
  const destroyAction = createDestroyKeyedStateAction(slice.name);

  return function keyedReducer(state: KeyedState<S> = {}, action: A): KeyedState<S> {
    const id = action.meta?.instanceID;

    // No instanceID. Ignore. Redux sends every action to every slice, so if we don't get any instanceID this action is not meant for us,
    // and we can just return early.
    if (!id) {
      return state; // no-op
    }

    if (destroyAction.match(action)) {
      // We cant mutate the current object (immer nuances), need to return a new object without the key we want to delete
      const newState: KeyedState<S> = {};
      entries(state).forEach(([key, value]) => {
        if (key !== id) {
          newState[key] = value;
        }
      });
      return newState;
    }

    // If the instance's state already exists, use that. Else, this is the first action for this ID, so we grab the initial state and use that.
    const prevInstanceState = state[id] ?? slice.getInitialState();
    const nextInstanceState = slice.reducer(prevInstanceState, action);

    if (prevInstanceState === nextInstanceState) {
      // If any change was done during the slice's reducer run, immer will create a new object for us.
      // If the prev and next objects are the same -- nothing has changed -- and we can avoid the spread below and just return the prev state.
      return state; // nothing changed
    }

    return { ...state, [id]: nextInstanceState };
  };
}
